import React from "react"
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightAddon,
  InputRightElement,
  Box,
  TypographyProps,
  CloseButton,
  Text, FormErrorMessage,
} from "@chakra-ui/react"
import {
  Control, FieldValues, useController,
} from "react-hook-form"

type Props<T extends FieldValues> = {
  id?: string
  name: string
  control: Control<T, object>
  size?: TypographyProps["fontSize"]
  fontWeight?: TypographyProps["fontWeight"]
  labelSize?: TypographyProps["fontSize"]
  type?: string
  variant?: "outline" | "filled" | "flushed" | "unstyled"
  onChange?: (event) => void
  onFocus?: (event) => void
  label?: string
  ariaLabel?: string
  description?: React.ReactNode
  labelWeight?: TypographyProps["fontWeight"]
  placeholder?: string
  required?: boolean
  autoComplete?: string
  disabled?: boolean
  error?: boolean
  errorText?: string | React.ReactNode
  helperText?: string | React.ReactNode
  onBlur?: (event) => void
  leftElement?: React.ReactNode
  rightElement?: React.ReactNode
  validate?: (value: string) => Promise<any>
  mb?: string|number
  px?: string|number
  color?: string
  vertical?: boolean
  readonly?: boolean
  h?: number
  showDeleteButton?: boolean
  onPaste?: (event: React.ClipboardEvent) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  onSelect?: (event: React.SyntheticEvent<HTMLInputElement>) => void
  onMouseUp?: (event: React.MouseEvent<HTMLInputElement>) => void
  errorMessage?: string
  showAsterisk?: boolean
  labelWidth?: number | string
  labelNoWrap?: boolean
  inputMaxW?: string | number
  fullWidth?: boolean
  maxLength?: number
  rightAddon?: React.ReactNode
}

const TextField = <T extends FieldValues, >({
  id,
  name,
  control,
  size = "md",
  fontWeight,
  labelSize = "md",
  type = "text",
  variant = "flushed",
  onChange = () => {},
  onFocus = () => {},
  label,
  ariaLabel,
  description,
  labelWeight,
  placeholder,
  autoComplete = "disabled",
  required,
  disabled,
  error,
  errorText,
  helperText,
  onBlur,
  leftElement,
  rightElement,
  validate,
  mb = 4,
  color = "",
  vertical,
  readonly,
  h = 10,
  px,
  showDeleteButton = false,
  onPaste,
  onKeyDown,
  onSelect,
  onMouseUp,
  errorMessage,
  showAsterisk,
  labelWidth,
  labelNoWrap,
  inputMaxW,
  fullWidth,
  maxLength,
  rightAddon,
}: Props<T>) => {
  const { field } = useController<any>({ name, control, rules: { validate } })
  const rightElementPadding = typeof rightElement === "string" && rightElement.length > 0
    ? `${rightElement.length + 3}ch`
    : undefined

  return (
    <FormControl
      display="flex"
      alignItems="flex-start"
      flexDirection={vertical ? "column" : "row"}
      isDisabled={disabled}
      mb={mb}
      isInvalid={error}
    >
      {label && (
      <FormLabel
        w={vertical ? "full" : (labelWidth ?? 1 / 4)}
        minW={labelNoWrap ? "max-content" : undefined}
        fontSize={labelSize}
        fontWeight={labelWeight}
        mt={vertical ? 0 : 2}
        whiteSpace={labelNoWrap ? "nowrap" : undefined}
        pr={labelNoWrap ? 2 : undefined}
      >
        {label}
        {showAsterisk && <Text as="span" color="red"> *</Text>}
      </FormLabel>
      )}
      <Box w="100%">
        {description && (
          <Text fontSize="md" mb={3}>
            {description}
          </Text>
        )}
        <InputGroup w={fullWidth ? "100%" : undefined}>
          {leftElement && <InputLeftElement zIndex={1} h={h}>{leftElement}</InputLeftElement>}
          <Input
            id={id}
            {...field}
            variant={variant}
            type={type}
            autoComplete={autoComplete}
            placeholder={placeholder}
            aria-label={ariaLabel}
            isRequired={required}
            w={fullWidth ? "100%" : undefined}
            maxW={fullWidth ? undefined : inputMaxW}
            onChange={(event) => {
              onChange(event)
              field.onChange(event)
            }}
            onBlur={(event) => {
              field.onBlur()
              onBlur?.(event)
            }}
            onFocus={onFocus}
            bg="white"
            fontSize={size}
            fontWeight={fontWeight}
            isReadOnly={readonly}
            color={color}
            _readOnly={{ color: "gray.500" }}
            h={h}
            onPaste={onPaste}
            onKeyDown={onKeyDown}
            onSelect={onSelect}
            onMouseUp={onMouseUp}
            px={px}
            pe={rightElementPadding ?? (showDeleteButton && field.value ? h : undefined)}
            maxLength={maxLength}
          />
          {rightElement && (
            <InputRightElement w="auto" pr={3} pointerEvents="none">
              <Text fontSize={size} color="gray.600" whiteSpace="nowrap">
                {rightElement}
              </Text>
            </InputRightElement>
          )}
          {(showDeleteButton && field.value) && (
            <InputRightElement h={h} w={h}>
              <CloseButton size="sm" color="gray.500" aria-label="Clear" onClick={() => { field.onChange("") }} />
            </InputRightElement>
          )}
          {rightAddon && <InputRightAddon h={h}>{rightAddon}</InputRightAddon>}
        </InputGroup>
        {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
        {error && errorText && (
          <FormHelperText as="div" mt={2}>
            <Text fontSize="md" fontWeight="semibold" color="red.500">{errorText}</Text>
          </FormHelperText>
        )}
        {!error && helperText && <FormHelperText whiteSpace="pre-line">{helperText}</FormHelperText>}
      </Box>
    </FormControl>
  )
}

export default TextField
